import React, { useContext, useEffect, useState } from "react";
import TabPanels from "../../../ui/tabs/TabPanels";
import Tabs from "../../../ui/tabs/Tabs";
import TabList from "../../../ui/tabs/TabList";
import { errorAlert, makeApiRequest, successAlert } from "../../../util";
import AuthContext from "../../../store/authContext";
import UpdateConnection from "./UpdateConnection";
import { getXSSIConnectionByRef } from "../../../api";
import UpdateUnitRanges from "../ranges/UpdateUnitRanges";
import InlineLoader from "../../loader/InlineLoader";
import UpdateGroupRanges from "../ranges/UpdateGroupRanges";
import {
  EDIT_XSSI_CONNECTION_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
} from "../../../translations/util";

interface IProps {
  org: string;
  xssiRef: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshTable: any;
}

const TABS_LIST = EDIT_XSSI_CONNECTION_TRANSLATIONS.tabList;

const EditXSSIConnection = ({ org, xssiRef, refreshTable }: IProps) => {
  const {
    token,
    scopes: { xssi_connections: xssiConnections },
  } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const [step, setStep] = useState(0);
  const [connectionData, setConnectionData] = useState({
    Ref: "",
    "CONNECTION-ID": "",
    CreationDate: "",
    ModificationDate: "",
    "RADIO-IPV4-ADDR": "",
    "RADIO-RFSS-ID": 1,
    "RADIO-SYSTEM-ID": 1,
    "RADIO-WACN-ID": 1,
    "RADIO-RFSS-4-HEX-DIGIT": false,
    "IWF-RFSS-ID": 1,
    "IWF-SYSTEM-ID": 1,
    "IWF-WACN-ID": 1,
    "RESPONSES-BEFORE-CONNECTED": 0,
    "MISSED-RESPONSES-BEFORE-DISCONNECTED": 0,
    "VOCODER-MODES": [],
    "UNIT-RANGES": [
      {
        "UNIT-MIN-ID": 1,
        "UNIT-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "IWF-GROUP-RANGES": [
      {
        "GROUP-MIN-ID": 1,
        "GROUP-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "RADIO-GROUP-RANGES": [
      {
        "GROUP-MIN-ID": 1,
        "GROUP-MAX-ID": 10,
        ID: 1,
        CreationDate: "",
        ModificationDate: "",
      },
    ],
    "POLLING-ENABLED": false,
    "POLLING-INTERVAL-CONNECTED": "",
    "POLLING-INTERVAL-DISCONNECTED": "",
  });

  const [initialConnectionData, setInitialConnectionData] =
    useState(connectionData);

  const getConnectionByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await getXSSIConnectionByRef({
          token,
          org,
          ref,
        });
        setConnectionData({
          Ref: response.Ref,
          "CONNECTION-ID": response["CONNECTION-ID"],
          CreationDate: response.CreationDate,
          ModificationDate: response.ModificationDate,
          "RADIO-IPV4-ADDR": response["RADIO-IPV4-ADDR"],
          "RADIO-RFSS-ID": response["RADIO-RFSS-ID"],
          "RADIO-SYSTEM-ID": response["RADIO-SYSTEM-ID"],
          "RADIO-WACN-ID": response["RADIO-WACN-ID"],
          "RADIO-RFSS-4-HEX-DIGIT": response["RADIO-RFSS-4-HEX-DIGIT"],
          "IWF-RFSS-ID": response["IWF-RFSS-ID"],
          "IWF-SYSTEM-ID": response["IWF-SYSTEM-ID"],
          "IWF-WACN-ID": response["IWF-WACN-ID"],
          "RESPONSES-BEFORE-CONNECTED": response["RESPONSES-BEFORE-CONNECTED"],
          "MISSED-RESPONSES-BEFORE-DISCONNECTED":
            response["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
          "VOCODER-MODES": response["VOCODER-MODES"],
          "UNIT-RANGES": response["UNIT-RANGES"],
          "IWF-GROUP-RANGES": response["IWF-GROUP-RANGES"],
          "RADIO-GROUP-RANGES": response["RADIO-GROUP-RANGES"],
          "POLLING-ENABLED": response["POLLING-ENABLED"],
          "POLLING-INTERVAL-CONNECTED": response["POLLING-INTERVAL-CONNECTED"],
          "POLLING-INTERVAL-DISCONNECTED":
            response["POLLING-INTERVAL-DISCONNECTED"],
        });
        setInitialConnectionData({
          Ref: response.Ref,
          "CONNECTION-ID": response["CONNECTION-ID"],
          CreationDate: response.CreationDate,
          ModificationDate: response.ModificationDate,
          "RADIO-IPV4-ADDR": response["RADIO-IPV4-ADDR"],
          "RADIO-RFSS-ID": response["RADIO-RFSS-ID"],
          "RADIO-SYSTEM-ID": response["RADIO-SYSTEM-ID"],
          "RADIO-WACN-ID": response["RADIO-WACN-ID"],
          "RADIO-RFSS-4-HEX-DIGIT": response["RADIO-RFSS-4-HEX-DIGIT"],
          "IWF-RFSS-ID": response["IWF-RFSS-ID"],
          "IWF-SYSTEM-ID": response["IWF-SYSTEM-ID"],
          "IWF-WACN-ID": response["IWF-WACN-ID"],
          "RESPONSES-BEFORE-CONNECTED": response["RESPONSES-BEFORE-CONNECTED"],
          "MISSED-RESPONSES-BEFORE-DISCONNECTED":
            response["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
          "VOCODER-MODES": response["VOCODER-MODES"],
          "UNIT-RANGES": response["UNIT-RANGES"],
          "IWF-GROUP-RANGES": response["IWF-GROUP-RANGES"],
          "RADIO-GROUP-RANGES": response["RADIO-GROUP-RANGES"],
          "POLLING-ENABLED": response["POLLING-ENABLED"],
          "POLLING-INTERVAL-CONNECTED": response["POLLING-INTERVAL-CONNECTED"],
          "POLLING-INTERVAL-DISCONNECTED":
            response["POLLING-INTERVAL-DISCONNECTED"],
        });
        refreshTable();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const updateXSSIConnections = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connections/${xssiRef}`,
        method: "PUT",
        body: JSON.stringify({
          "RADIO-IPV4-ADDR": connectionData["RADIO-IPV4-ADDR"],
          "RADIO-RFSS-ID": connectionData["RADIO-RFSS-ID"],
          "RADIO-SYSTEM-ID": connectionData["RADIO-SYSTEM-ID"],
          "RADIO-WACN-ID": connectionData["RADIO-WACN-ID"],
          "RADIO-RFSS-4-HEX-DIGIT": connectionData["RADIO-RFSS-4-HEX-DIGIT"],
          "IWF-RFSS-ID": connectionData["IWF-RFSS-ID"],
          "IWF-SYSTEM-ID": connectionData["IWF-SYSTEM-ID"],
          "IWF-WACN-ID": connectionData["IWF-WACN-ID"],
          "RESPONSES-BEFORE-CONNECTED":
            connectionData["RESPONSES-BEFORE-CONNECTED"],
          "MISSED-RESPONSES-BEFORE-DISCONNECTED":
            connectionData["MISSED-RESPONSES-BEFORE-DISCONNECTED"],
          // {"Message":"'test vocoder' is not one of ['FR', 'HR', 'Native', 'Phase 1']"}
          "VOCODER-MODES": connectionData["VOCODER-MODES"],
          "POLLING-ENABLED": connectionData["POLLING-ENABLED"],
          "POLLING-INTERVAL-CONNECTED":
            connectionData["POLLING-INTERVAL-CONNECTED"],
          "POLLING-INTERVAL-DISCONNECTED":
            connectionData["POLLING-INTERVAL-DISCONNECTED"],
        }),
        token,
        emptyResponse: true,
        org,
      });
      getConnectionByRef(xssiRef);
      successAlert(EDIT_XSSI_CONNECTION_TRANSLATIONS.successAlertMessage);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const resetConnectionChanges = () => {
    setConnectionData(initialConnectionData);
  };

  useEffect(() => {
    getConnectionByRef(xssiRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xssiRef]);

  return (
    <div className="data-tabs">
      {dataLoading ? (
        <InlineLoader
          description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
        />
      ) : (
        <Tabs
          selectedIndex={step}
          onChange={({ selectedIndex }: { selectedIndex: number }) =>
            setStep(selectedIndex)
          }
        >
          <div className="data-tablist">
            <TabList
              TABS_LIST={TABS_LIST}
              showBadge={false}
              ariaLabel="New Connection Form Data"
            />
          </div>

          <TabPanels>
            <UpdateConnection
              connectionData={connectionData}
              initialConnectionData={initialConnectionData}
              setConnectionData={setConnectionData}
              xssiRef={xssiRef}
              updateXSSIConnections={updateXSSIConnections}
              resetConnectionChanges={resetConnectionChanges}
              loading={loading}
              hasEditPermission={
                xssiConnections && xssiConnections.includes("u")
              }
            />
            <UpdateUnitRanges
              ranges={connectionData["UNIT-RANGES"]}
              xssiRef={xssiRef}
              org={org}
              refetchConnection={() => getConnectionByRef(xssiRef)}
              hasEditPermission={
                xssiConnections && xssiConnections.includes("u")
              }
              hasDeletePermission={
                xssiConnections && xssiConnections.includes("d")
              }
            />
            <UpdateGroupRanges
              ranges={connectionData["IWF-GROUP-RANGES"]}
              xssiRef={xssiRef}
              org={org}
              refetchConnection={() => getConnectionByRef(xssiRef)}
              hasEditPermission={
                xssiConnections && xssiConnections.includes("u")
              }
              hasDeletePermission={
                xssiConnections && xssiConnections.includes("d")
              }
              groupName="IWF"
            />
            <UpdateGroupRanges
              ranges={connectionData["RADIO-GROUP-RANGES"]}
              xssiRef={xssiRef}
              org={org}
              refetchConnection={() => getConnectionByRef(xssiRef)}
              hasEditPermission={
                xssiConnections && xssiConnections.includes("u")
              }
              hasDeletePermission={
                xssiConnections && xssiConnections.includes("d")
              }
              groupName="RADIO"
            />
          </TabPanels>
        </Tabs>
      )}
    </div>
  );
};

export default EditXSSIConnection;
